/**
 * QC (08 + 00c) — the Biologiste QC-governance surface FeatureModule. Four screens, all under /qc/*:
 *
 *   /qc/lots          — QC-lot enrolment (brouillon) + valider (freeze cible/ecart_type) + expiry sweep
 *   /qc/chart         — the Levey-Jennings chart (Recharts: ±1/2/3 SD bands + per-point violations) + the series table
 *   /qc/observation   — record a QC observation → auto-evaluate (Westgard), with the degraded "no valide lot" path
 *   /qc/holds         — the open-hold (rétention) worklist (estRetenu / lever) + open a manual hold
 *
 * Gating model. The four screens need DIFFERENT capabilities (lot.manage vs point.read vs controle.run
 * vs retenue.*), and the router applies ONE module-level gate per route. So — like inventory/finance —
 * we keep the MODULE gate empty (any authenticated user reaches the shell) and gate two ways:
 *   1. each NAV item carries its own requiredCapabilities (the side-nav shows only what you may open);
 *   2. each SCREEN renders its own ForbiddenState when its read/entry capability is missing, and every
 *      mutating action is gated by its exact server capability via useCan.
 * The server re-checks every request — useCan only hides/disables UI (never the trust boundary, L-4).
 *
 * QC is a LOCAL-deploy module that never leaves the node; where a build does not mount its routes a
 * probe 404s/501s and each screen renders a clean "non disponible sur cette installation" panel.
 *
 * Capabilities used (exact server keys, role BIOLOGISTE):
 *   qc.lot.manage · qc.point.read · qc.controle.run · qc.retenue.read|open|lift
 */
import type { FeatureModule } from "@/app";
import { QC_CAPS } from "./caps";
import { registerQcMessages } from "./i18n";
import { LotsPage } from "./screens/LotsPage";
import { ChartPage } from "./screens/ChartPage";
import { ObservationPage } from "./screens/ObservationPage";
import { HoldsPage } from "./screens/HoldsPage";

const qcModule: FeatureModule = {
  id: "qc",
  title: "Contrôle qualité",
  // Empty module gate (see header): per-screen ForbiddenState + per-nav-item caps do the gating.
  requiredCapabilities: [],
  routes: [
    { path: "/qc/lots", element: <LotsPage /> },
    { path: "/qc/chart", element: <ChartPage /> },
    { path: "/qc/observation", element: <ObservationPage /> },
    { path: "/qc/holds", element: <HoldsPage /> },
  ],
  nav: [
    {
      id: "qc-lots",
      path: "/qc/lots",
      label: "nav.qc.lots",
      icon: "▦",
      order: 60,
      section: "nav.section.qc",
      requiredCapabilities: [QC_CAPS.lotManage],
    },
    {
      id: "qc-chart",
      path: "/qc/chart",
      label: "nav.qc.chart",
      icon: "📈",
      order: 61,
      section: "nav.section.qc",
      requiredCapabilities: [QC_CAPS.pointRead],
    },
    {
      id: "qc-observation",
      path: "/qc/observation",
      label: "nav.qc.observation",
      icon: "✎",
      order: 62,
      section: "nav.section.qc",
      requiredCapabilities: [QC_CAPS.controleRun],
    },
    {
      id: "qc-holds",
      path: "/qc/holds",
      label: "nav.qc.holds",
      icon: "❚❚",
      order: 63,
      section: "nav.section.qc",
      requiredCapabilities: [QC_CAPS.retenueRead],
    },
  ],
  onRegister() {
    registerQcMessages();
  },
};

export default qcModule;
